import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

interface ICollection<T> {
  items: T[];
  loading: boolean;
  error?: string;
}

/**
 * Holds a list plus its loading/error flags — the whole of what EMMET's former
 * Akita entity domains actually did. Deliberately not a state-management
 * library: no normalization, no store/query split, no per-entity UI state. See
 * STEP-4C-PLAN.md.
 *
 * Compose it in a domain service rather than subclassing, so a domain exposes
 * only the observables it really has.
 */
export class CollectionState<T> {
  private readonly state$: BehaviorSubject<ICollection<T>>;
  private readonly initial: ICollection<T>;

  public readonly items$: Observable<T[]>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<string | undefined>;

  public constructor(loading = false) {
    this.initial = { items: [], loading };
    this.state$ = new BehaviorSubject(this.freshInitial());

    this.items$ = this.select(({ items }) => items);
    this.loading$ = this.select(({ loading: isLoading }) => isLoading);
    this.error$ = this.select(({ error }) => error);
  }

  /** `readonly` so callers cannot mutate the live array behind the observables. */
  public get items(): readonly T[] {
    return this.state$.value.items;
  }

  /**
   * Ends the load, because Akita's `set`/`add` did so implicitly (via
   * `setEntities`) and callers relied on it — most services call `setLoading(true)`
   * before fetching and never explicitly clear it.
   */
  public set(items: T[]): void {
    this.patch({ items, loading: false });
  }

  public add(...items: T[]): void {
    this.patch({ items: [...this.items, ...items], loading: false });
  }

  public replaceWhere(predicate: (item: T) => boolean, item: T): void {
    const existing = this.items.findIndex(predicate);
    const items = [...this.items];

    if (existing === -1) {
      items.push(item);
    } else {
      items[existing] = item;
    }

    this.patch({ items });
  }

  public updateWhere(predicate: (item: T) => boolean, partial: Partial<T>): void {
    this.patch({
      items: this.items.map((item) => (predicate(item) ? { ...item, ...partial } : item)),
    });
  }

  public removeWhere(predicate: (item: T) => boolean): void {
    this.patch({ items: this.items.filter((item) => !predicate(item)) });
  }

  /** Starting a load clears the previous error, matching the old store bases. */
  public setLoading(loading: boolean): void {
    this.patch(loading ? { loading, error: undefined } : { loading });
  }

  /**
   * Deliberately leaves `items` alone, unlike the Akita `CustomEntityStore` this
   * replaces, which cleared the collection on every error. Whether a stale list
   * should still render beside an error message is the template's call, not the
   * store's.
   */
  public setError(error: string | undefined): void {
    this.patch({ error, loading: false });
  }

  public reset(): void {
    this.state$.next(this.freshInitial());
  }

  public select<R>(project: (state: ICollection<T>) => R): Observable<R> {
    return this.state$.pipe(map(project), distinctUntilChanged());
  }

  private patch(partial: Partial<ICollection<T>>): void {
    this.state$.next({ ...this.state$.value, ...partial });
  }

  // A fresh array each time, so the baseline is never aliased by the live state —
  // a caller casting away `readonly` on `items` would otherwise corrupt `reset()`.
  private freshInitial(): ICollection<T> {
    return { ...this.initial, items: [...this.initial.items] };
  }
}
